import type { CalendarReminderDTO, CalendarRemindersResponse } from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Button, IconButton, useToast } from '../../components/ui';
import { useAuth } from '../../hooks/useAuth';
import { api, errorMessage } from '../../lib/api';
import { colorClasses } from '../../lib/colors';
import {
  DISMISSALS_STORAGE_KEY,
  dueReminders,
  nextReminderTime,
  parseDismissals,
  pruneDismissals,
  reminderHref,
  reminderKey,
  reminderTimeLabel,
  type Dismissals,
} from './reminderModel';
import { TASK_TYPE_ICONS, taskColor } from './taskAppearance';
import { invalidateTaskQueries } from './taskQueries';

const POLL_MS = 60_000;

function loadDismissals(): Dismissals {
  try {
    return parseDismissals(window.localStorage.getItem(DISMISSALS_STORAGE_KEY), Date.now());
  } catch {
    return {};
  }
}

function notificationPermission(): NotificationPermission {
  return typeof Notification === 'undefined' ? 'denied' : Notification.permission;
}

/** In-app reminders for my entries (assignee or guest). Mounted once in the app shell. */
export function ReminderCenter() {
  const { can } = useAuth();
  return can('lead:read') ? <ReminderStack /> : null;
}

// One empty list for "not loaded yet", so the timer effect below does not re-arm on every render.
const NO_REMINDERS: CalendarReminderDTO[] = [];

function ReminderStack() {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ['calendar', 'reminders'],
    queryFn: ({ signal }) => api.get<CalendarRemindersResponse>('/api/calendar/reminders', { signal }),
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
  });
  const [dismissed, setDismissed] = useState<Dismissals>(loadDismissals);
  const [now, setNow] = useState(() => Date.now());
  const [permission, setPermission] = useState(notificationPermission);
  // Keys already shown (on screen or as a desktop notification) in this session.
  const announced = useRef(new Set<string>());

  const items = data?.items ?? NO_REMINDERS;
  const due = dueReminders(items, now, dismissed);
  const dueKeys = due.map(reminderKey).join('|');

  // Keeps "in 5 minutes" fresh and fires reminders that come due between polls on time.
  useEffect(() => {
    const next = nextReminderTime(items, now, dismissed);
    const wait = next === null ? 30_000 : Math.min(30_000, Math.max(0, next - Date.now()) + 50);
    const timer = window.setTimeout(() => setNow(Date.now()), wait);
    return () => window.clearTimeout(timer);
  }, [items, now, dismissed]);

  const dismiss = (reminder: CalendarReminderDTO) => {
    setDismissed((current) => {
      const next = { ...pruneDismissals(current, Date.now()), [reminderKey(reminder)]: Date.now() };
      try {
        window.localStorage.setItem(DISMISSALS_STORAGE_KEY, JSON.stringify(next));
      } catch {
        // Without storage a dismissal only lasts for this tab.
      }
      return next;
    });
  };

  const open = (reminder: CalendarReminderDTO) => {
    dismiss(reminder);
    void navigate(reminderHref(reminder));
  };

  // A hidden tab cannot show the stack, so newly due reminders also go to the desktop (when allowed).
  useEffect(() => {
    for (const reminder of due) {
      const key = reminderKey(reminder);
      if (announced.current.has(key)) continue;
      announced.current.add(key);
      if (document.visibilityState !== 'hidden' || notificationPermission() !== 'granted') continue;
      const notification = new Notification(reminder.title, {
        body: [reminderTimeLabel(reminder, Date.now()), reminder.businessName].filter(Boolean).join(' · '),
        tag: key,
      });
      notification.onclick = () => {
        window.focus();
        open(reminder);
        notification.close();
      };
    }
    // `dueKeys` captures every change to `due`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dueKeys]);

  const complete = useMutation({
    mutationFn: (reminder: CalendarReminderDTO) => api.post(`/api/tasks/${reminder.id}/complete`, {}),
    onSuccess: async (_result, reminder) => {
      dismiss(reminder);
      toast.success('Marked as done');
      await invalidateTaskQueries(queryClient, reminder.businessId);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    // Bottom-left beside the sidebar so it never covers toasts (bottom-right); z-30 keeps it under panels and dialogs.
    <section
      aria-live="polite"
      aria-label="Reminders"
      className="pointer-events-none fixed bottom-4 left-4 z-30 flex w-[min(22rem,calc(100vw-2rem))] flex-col gap-2 lg:left-64"
    >
      {due.map((reminder) => {
        const Icon = TASK_TYPE_ICONS[reminder.type];
        return (
          <article
            key={reminderKey(reminder)}
            className="pointer-events-auto animate-pop-in rounded-lg border border-slate-200 bg-surface p-3 text-[13px] shadow-popover"
          >
            <div className="flex items-start gap-2">
              <Icon className={clsx('mt-0.5 size-4 shrink-0', colorClasses(taskColor(reminder)).text)} aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-slate-900">{reminder.title}</p>
                <p className="text-xs text-slate-600">
                  {reminderTimeLabel(reminder, now)}
                  {reminder.businessName && ` · ${reminder.businessName}`}
                </p>
              </div>
              <IconButton icon={X} size="sm" label={`Dismiss reminder for ${reminder.title}`} onClick={() => dismiss(reminder)} />
            </div>
            <div className="mt-2 flex justify-end gap-1.5">
              {reminder.type !== 'note' && (
                <Button size="sm" variant="ghost" loading={complete.isPending && complete.variables?.id === reminder.id} onClick={() => complete.mutate(reminder)}>
                  Mark done
                </Button>
              )}
              <Button size="sm" onClick={() => open(reminder)}>
                Open
              </Button>
            </div>
          </article>
        );
      })}
      {due.length > 0 && permission === 'default' && (
        <button
          type="button"
          className="pointer-events-auto self-start rounded-md bg-surface/90 px-2 py-1 text-xs text-slate-600 shadow-sm hover:text-ember-700"
          onClick={() => void Notification.requestPermission().then(setPermission)}
        >
          Enable desktop notifications
        </button>
      )}
    </section>
  );
}
